import { Component, DoCheck, Input, KeyValueDiffers, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Clipboard } from '@angular/cdk/clipboard';
import { ChatManager, ChatService } from '../../../../../services/chat.service';
import { CHAT_SUB_TYPES, entityIcons, FUNNEL_TYPE, activeTranslations } from '../../../../../enum/constants';
import { BaseService } from '../../../../../services/base.service';
import { IChat, IColumnCard } from '../../../../../interfaces';
import { parseCurrencyXXX } from '../../../../../helpers';
import { finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { TOOLS } from './chat-about.interface';
import { OneFieldComponent } from '../../../../../dialogs/one-field/one-field.component';
import { ModalService } from '../../../../../services/modal.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { ChatTag, TagService } from '../../../../../services/tag.service';
import { StackedSidebarService } from '../../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../stacked-sidebar/stacked-sidebar.interface';

@Component({
  selector: 'app-chat-about',
  templateUrl: './chat-about.component.html',
  styleUrl: './chat-about.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAboutComponent implements OnInit, DoCheck, OnDestroy {
  @Input() chat!: IChat;
  private differ: any;
  private readonly destroy$ = new Subject<null>();
  protected tags: ChatTag[] = [];
  protected tagSearchTerm = '';
  protected isTagsPickerOpen = false;
  protected readonly updatingTagIds = new Set<string>();
  protected isCreatingLeadCard = false;
  protected managers: ChatManager[] = [];
  protected managerSearchTerm = '';
  protected isManagersPickerOpen = false;
  protected updatingManagerId: string | null | undefined;

  constructor(
    private readonly modalService: ModalService,
    private readonly differs: KeyValueDiffers,
    protected readonly chatService: ChatService,
    protected readonly baseService: BaseService,
    private readonly clipboard: Clipboard,
    private readonly notificationService: NotificationService,
    private readonly tagService: TagService,
    private readonly stackedSidebarService: StackedSidebarService,
  ) {}

  ngOnInit(): void {
    if (this.chat) {
      this.differ = this.differs.find(this.chat).create();
    }

    this.tagService
      .getTags()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: (tags) => (this.tags = tags), error: () => (this.tags = []) });

  }

  ngDoCheck(): void {
    if (this.differ) {
      const changes = this.differ.diff(this.chat);
      if (changes) {
        this.chatService.initActions();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  protected openDeal(deal: IColumnCard) {
    const card = deal as IColumnCard & { funnel_type?: string; crm_version?: string };
    const isLead = card.funnel_type === 'LEAD';

    if (card.crm_version === '2') {
      this.stackedSidebarService
        .openSidebar(
          {
            type: SidebarType.CardV3,
            width: '90vw',
          },
          {
            card_id: deal.id,
            type: isLead ? FUNNEL_TYPE.LEAD : FUNNEL_TYPE.DEAL,
          },
        )
        .subscribe();
      return;
    }

    const url = `${location.origin}/crm/deals?deal=${deal.id}`;
    this.baseService.openSite(url);
  }

  protected openHistory(): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.ChatTimeline, width: '520px' }, { chatId: this.chat.id, chatTitle: this.chat.title })
      .subscribe();
  }

  protected createLeadCard(): void {
    if (!this.chat?.id || this.isCreatingLeadCard || this.hasLinkedDeals) return;

    this.isCreatingLeadCard = true;
    this.chatService
      .createLeadCard(this.chat.id)
      .pipe(
        switchMap(() => this.chatService.getDealByChat()),
        finalize(() => (this.isCreatingLeadCard = false)),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          this.notificationService.showSuccess(
            '',
            activeTranslations.notifications_texts.success.successfully_created,
          );
        },
        error: () => {
          this.notificationService.showError(
            '',
            activeTranslations.notifications_texts.error.error_create_card,
          );
        },
      });
  }

  protected get hasLinkedDeals(): boolean {
    return !!this.chatService.deals?.length;
  }

  protected closeRightMenu() {
    this.chatService.closeRightMenu();
  }

  protected onChangeTitleChat() {
    const modalData = {
      title: 'change_name',
      translate: true,
      component: OneFieldComponent,
      data: {
        input: this.chatService.chat.title,
      },
    };

    this.modalService
      .openModal(modalData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((title) => {
        if (title) {
          this.chatService
            .updateChat(this.chatService.selectedChatId, { title })
            .pipe(takeUntil(this.destroy$))
            .subscribe();
        }
      });
  }

  protected copyActionValue(event: MouseEvent, value?: string) {
    event?.stopPropagation();
    if (!value) return;

    if (this.clipboard.copy(value)) {
      this.notificationService.showInfo(
        '',
        activeTranslations.notifications_texts.info.copied,
      );
    }
  }

  protected toggleTagsPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.isTagsPickerOpen = !this.isTagsPickerOpen;
  }

  protected isTagAssigned(tagId: string): boolean {
    return !!this.chat.tags?.some((tag) => tag.id === tagId);
  }

  protected isTagUpdating(tagId: string): boolean {
    return this.updatingTagIds.has(tagId);
  }

  protected get filteredTags(): ChatTag[] {
    const searchTerm = this.tagSearchTerm.trim().toLocaleLowerCase();
    if (!searchTerm) return this.tags;

    return this.tags.filter((tag) => tag.name.toLocaleLowerCase().includes(searchTerm));
  }

  protected toggleChatTag(tagId: string, event: MouseEvent): void {
    event.stopPropagation();
    if (this.updatingTagIds.size) return;
    if (!this.chat?.id) return;

    const selectedTagIds = this.chat.tags?.map((tag) => tag.id) || [];
    const nextTagIds = this.isTagAssigned(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId];

    this.updatingTagIds.add(tagId);
    this.chatService
      .updateChatTags(this.chat.id, nextTagIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.updatingTagIds.delete(tagId);
        },
        error: () => this.updatingTagIds.delete(tagId),
      });
  }

  protected toggleManagersPicker(event: MouseEvent): void {
    event.stopPropagation();
    this.isManagersPickerOpen = !this.isManagersPickerOpen;
    this.managers = [];
    if (!this.isManagersPickerOpen) return;
    const chatId = this.chat.id;
    this.chatService.getAssignableManagers(chatId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((managers) => {
        if (this.chat.id === chatId && this.isManagersPickerOpen) this.managers = managers;
      });
  }

  protected isManagerAssigned(managerId: string | null): boolean {
    return (this.chat.assigned_user?.id ?? null) === managerId;
  }

  protected get filteredManagers(): ChatManager[] {
    const searchTerm = this.managerSearchTerm.trim().toLocaleLowerCase();
    if (!searchTerm) return this.managers;

    return this.managers.filter((manager) => manager.name.toLocaleLowerCase().includes(searchTerm));
  }

  protected changeChatManager(managerId: string | null, event: MouseEvent): void {
    event.stopPropagation();
    if (this.updatingManagerId !== undefined) return;
    if (!this.chat?.id || this.isManagerAssigned(managerId)) return;

    this.updatingManagerId = managerId;
    this.chatService
      .updateChat(this.chat.id, { assigned_user_id: managerId })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.updatingManagerId = undefined)),
      )
      .subscribe({
        next: () => (this.isManagersPickerOpen = false),
        // The chat may become inaccessible after reassigning it to another manager (chat distribution)
        error: () => this.chatService.reloadChatList(),
      });
  }

  protected readonly parseCurrencyXXX = parseCurrencyXXX;
  protected readonly Object = Object;
  protected readonly TOOLS = TOOLS;
  protected readonly CHAT_SUB_TYPES = CHAT_SUB_TYPES;
  protected readonly entityIcons = entityIcons;
}
